import React from 'react';

import {
  COLORS,
  COLOR_MODE_KEY,
  INITIAL_COLOR_MODE_CSS_PROP,
} from '../constants';

export const ThemeContext = React.createContext<{
  colorMode: string;
  setColorMode: (mode: string) => void;
} | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [colorMode, rawSetColorMode] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    const root = window.document.documentElement;

    // Set initial color mode from system preference or localStorage
    const savedColorMode = localStorage.getItem(COLOR_MODE_KEY);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialMode = savedColorMode || (prefersDark ? 'dark' : 'light');

    // Apply initial colors
    Object.entries(COLORS).forEach(([name, colorByTheme]) => {
      const cssVarName = `--color-${name}`;
      root.style.setProperty(cssVarName, colorByTheme[initialMode as keyof typeof colorByTheme]);
    });

    rawSetColorMode(initialMode);
  }, []);

  const contextValue = React.useMemo(() => {
    function setColorMode(newValue: string) {
      const root = window.document.documentElement;

      localStorage.setItem(COLOR_MODE_KEY, newValue);

      Object.entries(COLORS).forEach(([name, colorByTheme]) => {
        const cssVarName = `--color-${name}`;
        root.style.setProperty(cssVarName, colorByTheme[newValue as keyof typeof colorByTheme]);
      });

      rawSetColorMode(newValue);
    }

    return {
      colorMode: colorMode || 'light',
      setColorMode,
    };
  }, [colorMode, rawSetColorMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};